import { Injectable } from '@angular/core';
import { CommonDataParamsService } from './common-data-params.service';
import {
  ScoreTimeSeriesRequestBody,
  ScoreTimeSeriesEventFilter,
  ScoreTimeSeriesTimeRange,
  ScoreRequestTypes,
  ScoreType,
  CustomScore,
} from '../models/score-time-series.interface';
import getScoreTypeDetails from './score-type-details';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';

/**
 * Parameters for building score time series request
 * NOTE: Only 'entities' type is supported (no search_query)
 *
 * @example Standalone usage (explicit values)
 * {
 *   score_type: 'esg',
 *   entity: ['bmw'],
 *   entityType: 'company',
 *   time: '2025-07-24--2025-10-24'
 * }
 *
 * @example Query params usage (falls back to URL params)
 * {
 *   score_type: 'esg'
 *   // entity, entityType, time will be resolved from query params
 * }
 *
 * @example Mixed usage (override some, use query params for others)
 * {
 *   score_type: 'esg',
 *   time: '30'  // override time, use query params for entity
 * }
 */
export interface ScoreTimeSeriesContentParams {
  score_type: ScoreRequestTypes;
  collapse_on?: 'participant'[];
  entity?: string[];
  entityType?: string;
  time?: string;
  timePeriod?: string;
  newsday?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ScoreUtilsService {
  constructor(
    private commonDataParams: CommonDataParamsService,
    private featureFlagsService: FeatureFlagsService,
  ) {}

  // ============================================================================
  // Custom Score Resolution Methods
  // ============================================================================

  /**
   * Maps a user-facing score ID to the canonical API score key
   * (`credit_risk` → `credit_watch`, etc.). Falls back to the input id
   * for unrecognized values (e.g. custom score uids), matching how
   * `getScoreTypeDetails` behaves at the call sites.
   */
  static resolveScoreKey(scoreId: string): string {
    return getScoreTypeDetails(scoreId).scoreKey || scoreId;
  }

  /**
   * Checks if a score ID is a predefined score type (not custom).
   * Uses getScoreTypeDetails mapping as the single source of truth.
   */
  isPredefinedScore(scoreId: string): boolean {
    const details = getScoreTypeDetails(scoreId);
    return !!details.scoreKey;
  }

  /**
   * Resolves any score ID to API-compatible format.
   * - Predefined scores: returns as ScoreType string
   * - Custom scores: transforms to CustomScore object with event_weights
   */
  resolveScoreType(scoreId: string): ScoreRequestTypes {
    if (this.isPredefinedScore(scoreId)) {
      return scoreId as ScoreType;
    }
    return this.transformCustomScore(scoreId);
  }

  /**
   * Single source of truth for custom-score weights: signed `weight * polarity`
   * (defaults `5` / `-1`) + the `with_black_swan` flag. Wrappers below serialize
   * per endpoint. Returns `null` for non-custom scores or empty weights.
   */
  private normalizeCustomScore(scoreUid: string): {
    weights: { event_id: string; weight: number; persistence: number }[];
    withBlackSwan: boolean;
  } | null {
    const customScore =
      this.featureFlagsService.scorelabSettings.customScores?.find(
        (score: { uid: string }) => score.uid === scoreUid,
      );

    if (!customScore?.event_weights?.length) {
      return null;
    }

    const weights = customScore.event_weights
      .filter((event: { event_id?: string }) => !!event.event_id)
      .map(
        (event: {
          event_id: string;
          weight?: number;
          polarity?: number;
          weight_persistence?: number;
        }) => ({
          event_id: event.event_id,
          weight: (event.weight ?? 5) * (event.polarity ?? -1),
          persistence: event.weight_persistence ?? 90,
        }),
      );

    if (!weights.length) {
      return null;
    }

    return {
      weights,
      withBlackSwan: customScore.sentiment_type === 'neg',
    };
  }

  /**
   * `event_weights` array body for the v2 `score_ts` POST (no `with_black_swan`
   * there). Falls back to `general_risk` for non-custom / weightless scores.
   */
  private transformCustomScore(scoreUid: string): CustomScore | ScoreType {
    const normalized = this.normalizeCustomScore(scoreUid);
    if (!normalized) {
      return 'general_risk';
    }
    return {
      event_weights: normalized.weights.map(({ event_id, weight }) => ({
        event_id,
        weight,
      })),
    };
  }

  /**
   * Flattened `{event_id}_weight` query params + `with_black_swan` for the GET
   * `score_ts_top_events` endpoint. `null` for non-custom scores (caller then
   * sends `score_type`).
   */
  getCustomScoreEventWeights(scoreUid: string): {
    eventWeights: Record<string, number>;
    withBlackSwan: boolean;
  } | null {
    const normalized = this.normalizeCustomScore(scoreUid);
    if (!normalized) {
      return null;
    }

    const eventWeights: Record<string, number> = {};
    normalized.weights.forEach(({ event_id, weight }) => {
      eventWeights[`${event_id}_weight`] = weight;
    });

    return { eventWeights, withBlackSwan: normalized.withBlackSwan };
  }

  /**
   * Score-alert trigger params: `{event_id}_weight: "<signed weight>,<persistence>"`
   * + `with_black_swan`. The alerting service only runs on this copy, so the
   * alert dialog and the Score Lab re-sync must both build it here.
   */
  getCustomScoreAlertParams(scoreUid: string): Record<string, string> | null {
    const normalized = this.normalizeCustomScore(scoreUid);
    if (!normalized) {
      return null;
    }

    const params: Record<string, string> = {};
    normalized.weights.forEach(({ event_id, weight, persistence }) => {
      params[`${event_id}_weight`] = `${weight},${persistence}`;
    });
    if (normalized.withBlackSwan) {
      params['with_black_swan'] = 'true';
    }
    return params;
  }

  // ============================================================================
  // Score Time Series Utilities
  // ============================================================================

  /**
   * Returns the last non-null score from a time series array.
   */
  static getLastScore(
    timeSeries: Array<{ score: number | null }>,
  ): number | null {
    for (let i = timeSeries.length - 1; i >= 0; i--) {
      if (timeSeries[i].score !== null && timeSeries[i].score !== undefined) {
        return timeSeries[i].score;
      }
    }
    return null;
  }

  /**
   * Calculates bull/bear trend from a score value.
   * Centralized so all consumers (news, scanner, etc.) use the same logic.
   */
  static getBullBearTrend(score: number | null): {
    trendType: 'bull' | 'bear' | null;
    trendValue: string | null;
  } {
    if (score === null) {
      return { trendType: null, trendValue: null };
    }
    return {
      trendType: score > 5.0 ? 'bull' : 'bear',
      trendValue: score.toFixed(1),
    };
  }

  // ============================================================================
  // Request Building Methods
  // ============================================================================

  private resolveTimeRange(
    params: ScoreTimeSeriesContentParams,
  ): ScoreTimeSeriesTimeRange {
    return this.commonDataParams.generateTimeRange({
      type: 'entities',
      time: params.time,
      timePeriod: params.timePeriod,
      newsday: params.newsday,
    });
  }

  private createEventFilter(
    eventParticipants: string[],
    params: ScoreTimeSeriesContentParams,
  ): ScoreTimeSeriesEventFilter {
    const eventFilter: ScoreTimeSeriesEventFilter = {};

    if (eventParticipants.length > 0) {
      eventFilter.event_participants = eventParticipants;
    }

    if (!params.entity) {
      const queryParams = this.commonDataParams.getCurrentQueryParams();

      const eventIds = this.commonDataParams.getEventIds(queryParams);
      if (eventIds) {
        eventFilter.event_ids = eventIds;
      }

      if (queryParams.temporality) {
        eventFilter.temporality = queryParams.temporality.split(',');
      }

      if (queryParams.factuality) {
        eventFilter.factuality = this.commonDataParams.getFactualityValues(
          queryParams.factuality,
        );
      }
    }

    return eventFilter;
  }

  private resolveApiScoreType(scoreType: ScoreRequestTypes): ScoreRequestTypes {
    if (typeof scoreType === 'object') {
      return scoreType;
    }

    const details = getScoreTypeDetails(scoreType);
    return (details.scoreKey || scoreType) as ScoreRequestTypes;
  }

  /**
   *
   * @param apiOverrides - Optional API fields to override (e.g., collapse_on)
   * @param params - Content params for building the request
   */
  createScoreTimeSeriesRequest(
    apiOverrides: Partial<ScoreTimeSeriesRequestBody>,
    params: ScoreTimeSeriesContentParams,
  ): ScoreTimeSeriesRequestBody {
    const apiScoreType = this.resolveApiScoreType(params.score_type);
    const eventParticipants = this.commonDataParams.getEventParticipants({
      entity: params.entity,
      entityType: params.entityType,
    });
    const eventFilter = this.createEventFilter(eventParticipants, params);

    const requestBody: ScoreTimeSeriesRequestBody = {
      score_type: apiScoreType,
      time_range: this.resolveTimeRange(params),
    };

    if (Object.keys(eventFilter).length > 0) {
      requestBody.event_filter = eventFilter;
    }

    if (params.collapse_on) {
      requestBody.collapse_on = params.collapse_on;
    }

    return {
      ...requestBody,
      ...apiOverrides,
    };
  }
}
